/**
 * Leitura de NF-e/NFC-e (leiaute 4.00) para o confronto.
 * Aceita nfeProc, NFe solta ou envelopada (SOAP, lote enviNFe): procura <NFe> em qualquer nível.
 */
import type { EnderecoLido, IbsCbsLido, IcmsLido, IssqnLido, ItemLido, NotaLida, TributoValorLido } from './tipos';
import { buscar, filho, filhos, lerXml, valor, type NoXml } from './xml';

export class ArquivoInvalido extends Error {}

const numero = (s: string | null): number | null => {
  if (s === null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** Primeiro grupo filho de <ICMS> (ICMS00, ICMS40, ICMSSN102, ICMSPart, ICMSST...). */
function lerIcms(icms: NoXml | null): IcmsLido | null {
  const grupo = icms?.filhos[0];
  if (!grupo) return null;
  const csosn = valor(grupo, 'CSOSN');
  return {
    grupo: grupo.nome,
    tabela: csosn !== null ? 'ICMS_CSOSN' : 'ICMS_CST',
    cst: csosn ?? valor(grupo, 'CST'),
    orig: valor(grupo, 'orig'),
    modBC: valor(grupo, 'modBC'),
    modBCST: valor(grupo, 'modBCST'),
    motDesICMS: valor(grupo, 'motDesICMS'),
    motDesICMSST: valor(grupo, 'motDesICMSST'),
    temStRetido: valor(grupo, 'vBCSTRet') !== null || valor(grupo, 'vICMSSTRet') !== null,
    pMVAST: numero(valor(grupo, 'pMVAST')),
    vBCST: numero(valor(grupo, 'vBCST')),
    pRedBCST: numero(valor(grupo, 'pRedBCST')),
    pICMSST: numero(valor(grupo, 'pICMSST')),
    vICMSST: numero(valor(grupo, 'vICMSST')),
    vBC: numero(valor(grupo, 'vBC')),
    pICMS: numero(valor(grupo, 'pICMS')),
    vICMS: numero(valor(grupo, 'vICMS')),
    vICMSOp: numero(valor(grupo, 'vICMSOp')),
    pDif: numero(valor(grupo, 'pDif')),
    vICMSDif: numero(valor(grupo, 'vICMSDif')),
  };
}

/** Subgrupo com CST dentro de <IPI>/<PIS>/<COFINS> (IPITrib, IPINT, PISAliq, PISNT, PISOutr...). */
function subgrupoComCst(no: NoXml | null): NoXml | null {
  if (!no) return null;
  for (const g of no.filhos) {
    if (valor(g, 'CST') !== null) return g;
  }
  return null;
}

/**
 * CST, base, alíquota (%) e valor de um tributo simples (IPI, PIS, COFINS). Ausente quando o subgrupo
 * não declara o campo (ex.: IPINT, PISNT — não tributados — não têm vBC/alíquota/valor).
 */
function lerTributoValor(no: NoXml | null, campoAliquota: string, campoValor: string): TributoValorLido | null {
  const grupo = subgrupoComCst(no);
  if (!grupo) return null;
  return {
    cst: valor(grupo, 'CST'),
    vBC: numero(valor(grupo, 'vBC')),
    pAliquota: numero(valor(grupo, campoAliquota)),
    vTributo: numero(valor(grupo, campoValor)),
  };
}

function lerItem(det: NoXml): ItemLido {
  const prod = filho(det, 'prod');
  const imposto = filho(det, 'imposto');
  const ipi = filho(imposto, 'IPI');
  const pis = filho(imposto, 'PIS');
  const cofins = filho(imposto, 'COFINS');
  const ibscbs = filho(imposto, 'IBSCBS');
  const icmsUFDest = filho(imposto, 'ICMSUFDest');
  return {
    nItem: Number(det.attrs.nItem) || 0,
    cProd: valor(prod, 'cProd'),
    xProd: valor(prod, 'xProd'),
    ncm: valor(prod, 'NCM'),
    cest: valor(prod, 'CEST'),
    cBenef: valor(prod, 'cBenef'),
    cfop: valor(prod, 'CFOP'),
    vProd: numero(valor(prod, 'vProd')),
    indTot: valor(prod, 'indTot') !== '0',
    qCom: numero(valor(prod, 'qCom')),
    uCom: valor(prod, 'uCom'),
    vUnCom: numero(valor(prod, 'vUnCom')),
    vDesc: numero(valor(prod, 'vDesc')),
    vFrete: numero(valor(prod, 'vFrete')),
    vSeg: numero(valor(prod, 'vSeg')),
    vOutro: numero(valor(prod, 'vOutro')),
    vII: numero(valor(prod, 'vII')),
    icms: lerIcms(filho(imposto, 'ICMS')),
    icmsUFDest: icmsUFDest
      ? {
          vBCUFDest: numero(valor(icmsUFDest, 'vBCUFDest')),
          pFCPUFDest: numero(valor(icmsUFDest, 'pFCPUFDest')),
          pICMSUFDest: numero(valor(icmsUFDest, 'pICMSUFDest')),
          pICMSInter: numero(valor(icmsUFDest, 'pICMSInter')),
          pICMSInterPart: numero(valor(icmsUFDest, 'pICMSInterPart')),
          vFCPUFDest: numero(valor(icmsUFDest, 'vFCPUFDest')),
          vICMSUFDest: numero(valor(icmsUFDest, 'vICMSUFDest')),
        }
      : null,
    ipi: ipi ? { ...(lerTributoValor(ipi, 'pIPI', 'vIPI') ?? { cst: null, vBC: null, pAliquota: null, vTributo: null }), cEnq: valor(ipi, 'cEnq') } : null,
    pis: lerTributoValor(pis, 'pPIS', 'vPIS'),
    cofins: lerTributoValor(cofins, 'pCOFINS', 'vCOFINS'),
    ibscbs: lerIbsCbs(ibscbs),
    issqn: lerIssqn(filho(imposto, 'ISSQN')),
  };
}

/** Grupo ISSQN do item (NF-e conjugada). */
function lerIssqn(no: NoXml | null): IssqnLido | null {
  if (!no) return null;
  return {
    vBC: numero(valor(no, 'vBC')),
    vAliq: numero(valor(no, 'vAliq')),
    vISSQN: numero(valor(no, 'vISSQN')),
    cMunFG: valor(no, 'cMunFG'),
    cListServ: valor(no, 'cListServ'),
    indISS: valor(no, 'indISS'),
  };
}

/** IBSCBS do item: gIBSCBS traz a base e os subgrupos gIBSUF, gIBSMun e gCBS (cada um com gRed opcional). */
function lerIbsCbs(no: NoXml | null): IbsCbsLido | null {
  if (!no) return null;
  const g = filho(no, 'gIBSCBS');
  const uf = filho(g, 'gIBSUF');
  const mun = filho(g, 'gIBSMun');
  const cbs = filho(g, 'gCBS');
  const reducao = (sub: NoXml | null) => numero(valor(filho(sub, 'gRed'), 'pRedAliq'));
  return {
    cst: valor(no, 'CST'),
    cClassTrib: valor(no, 'cClassTrib'),
    vBC: numero(valor(g, 'vBC')),
    pIBSUF: numero(valor(uf, 'pIBSUF')),
    vIBSUF: numero(valor(uf, 'vIBSUF')),
    pRedIBSUF: reducao(uf),
    pIBSMun: numero(valor(mun, 'pIBSMun')),
    vIBSMun: numero(valor(mun, 'vIBSMun')),
    pRedIBSMun: reducao(mun),
    pCBS: numero(valor(cbs, 'pCBS')),
    vCBS: numero(valor(cbs, 'vCBS')),
    pRedCBS: reducao(cbs),
  };
}

function lerEndereco(no: NoXml | null): EnderecoLido | null {
  if (!no) return null;
  return {
    xLgr: valor(no, 'xLgr'),
    nro: valor(no, 'nro'),
    xCpl: valor(no, 'xCpl'),
    xBairro: valor(no, 'xBairro'),
    cMun: valor(no, 'cMun'),
    xMun: valor(no, 'xMun'),
    uf: valor(no, 'UF'),
    cep: valor(no, 'CEP'),
    fone: valor(no, 'fone'),
  };
}

function lerNota(nfe: NoXml, arquivo: string, protNfe?: NoXml | null): NotaLida {
  const inf = filho(nfe, 'infNFe');
  if (!inf) throw new ArquivoInvalido('NF-e sem o grupo infNFe');
  const chave = (inf.attrs.Id ?? '').replace(/^NFe/, '');
  if (!/^[0-9A-Z]{44}$/.test(chave)) throw new ArquivoInvalido('NF-e sem chave de acesso válida (atributo Id do infNFe)');
  const ide = filho(inf, 'ide');
  const emit = filho(inf, 'emit');
  const dest = filho(inf, 'dest');
  const dhEmissao = valor(ide, 'dhEmi') ?? valor(ide, 'dEmi');
  const icmsTot = filho(filho(inf, 'total'), 'ICMSTot');
  const issqnTot = filho(filho(inf, 'total'), 'ISSQNtot');
  const itens = filhos(inf, 'det').map(lerItem);
  if (!itens.length) throw new ArquivoInvalido('NF-e sem itens (det)');
  const transp = filho(inf, 'transp');
  const transporta = filho(transp, 'transporta');
  const vol = filho(transp, 'vol');
  const infProt = protNfe ? filho(protNfe, 'infProt') : null;
  const infAdic = filho(inf, 'infAdic');
  return {
    arquivo,
    chave,
    modelo: valor(ide, 'mod'),
    serie: valor(ide, 'serie'),
    numero: valor(ide, 'nNF'),
    dhEmissao,
    dataEmissao: dhEmissao?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? null,
    natOp: valor(ide, 'natOp'),
    emit: {
      cnpj: valor(emit, 'CNPJ') ?? valor(emit, 'CPF'),
      nome: valor(emit, 'xNome'),
      uf: valor(filho(emit, 'enderEmit'), 'UF'),
      crt: valor(emit, 'CRT'),
      ie: valor(emit, 'IE'),
      enderEmit: lerEndereco(filho(emit, 'enderEmit')),
    },
    dest: {
      doc: valor(dest, 'CNPJ') ?? valor(dest, 'CPF') ?? valor(dest, 'idEstrangeiro'),
      nome: valor(dest, 'xNome'),
      uf: valor(filho(dest, 'enderDest'), 'UF'),
      indIEDest: valor(dest, 'indIEDest'),
      ie: valor(dest, 'IE'),
      enderDest: lerEndereco(filho(dest, 'enderDest')),
    },
    tpNF: valor(ide, 'tpNF'),
    finNFe: valor(ide, 'finNFe'),
    idDest: valor(ide, 'idDest'),
    indFinal: valor(ide, 'indFinal'),
    indPres: valor(ide, 'indPres'),
    vNF: numero(valor(icmsTot, 'vNF')),
    totais: {
      vProd: numero(valor(icmsTot, 'vProd')),
      vBC: numero(valor(icmsTot, 'vBC')),
      vICMS: numero(valor(icmsTot, 'vICMS')),
      vIPI: numero(valor(icmsTot, 'vIPI')),
      vPIS: numero(valor(icmsTot, 'vPIS')),
      vCOFINS: numero(valor(icmsTot, 'vCOFINS')),
    },
    totaisIssqn: issqnTot
      ? {
          vServ: numero(valor(issqnTot, 'vServ')),
          vBC: numero(valor(issqnTot, 'vBC')),
          vISS: numero(valor(issqnTot, 'vISS')),
          vPIS: numero(valor(issqnTot, 'vPIS')),
          vCOFINS: numero(valor(issqnTot, 'vCOFINS')),
        }
      : null,
    itens,
    transp: transp
      ? {
          modFrete: valor(transp, 'modFrete'),
          transporta: transporta
            ? { doc: valor(transporta, 'CNPJ') ?? valor(transporta, 'CPF'), xNome: valor(transporta, 'xNome'), ie: valor(transporta, 'IE'), uf: valor(transporta, 'UF') }
            : null,
          vol: vol ? { qVol: valor(vol, 'qVol'), esp: valor(vol, 'esp'), pesoL: valor(vol, 'pesoL'), pesoB: valor(vol, 'pesoB') } : null,
        }
      : null,
    pag: filhos(filho(inf, 'pag'), 'detPag').map((p) => ({ tPag: valor(p, 'tPag'), vPag: numero(valor(p, 'vPag')) })),
    protocolo: infProt ? { nProt: valor(infProt, 'nProt'), dhRecbto: valor(infProt, 'dhRecbto') } : null,
    infAdic: infAdic ? { infCpl: valor(infAdic, 'infCpl') } : null,
  };
}

/** Todas as NF-e de um arquivo (um lote enviNFe pode ter várias). */
export function lerArquivoNfe(conteudo: string, arquivo: string): NotaLida[] {
  let raiz: NoXml;
  try {
    raiz = lerXml(conteudo);
  } catch (e) {
    throw new ArquivoInvalido(`XML inválido: ${e instanceof Error ? e.message : String(e)}`);
  }
  const procs = buscar(raiz, 'nfeProc');
  if (procs.length) {
    return procs.flatMap((proc) => filhos(proc, 'NFe').map((n) => lerNota(n, arquivo, filho(proc, 'protNFe'))));
  }
  const nfes = buscar(raiz, 'NFe');
  if (!nfes.length) throw new ArquivoInvalido('O arquivo não contém NF-e (elemento NFe)');
  return nfes.map((n) => lerNota(n, arquivo));
}

/**
 * Extrai o XML bruto de cada NF-e do arquivo, indexado pela chave de acesso — preserva o texto
 * byte-original (não reserializa a árvore), para armazenamento, DANFE e validador externo.
 * Prefere blocos <nfeProc>...</nfeProc> (incluem o protocolo de autorização); sem nfeProc, usa <NFe>...</NFe>.
 */
export function extrairXmlPorChave(conteudo: string): Map<string, string> {
  const saida = new Map<string, string>();
  const extrairBlocos = (tag: string) => {
    const rx = new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'g');
    return conteudo.match(rx) ?? [];
  };
  const chaveDoBloco = (bloco: string): string | null => {
    const m = bloco.match(/Id\s*=\s*"(?:NFe)?([0-9A-Z]{44})"/);
    return m ? m[1] : null;
  };
  const blocosProc = extrairBlocos('nfeProc');
  const blocos = blocosProc.length ? blocosProc : extrairBlocos('NFe');
  for (const bloco of blocos) {
    const chave = chaveDoBloco(bloco);
    if (chave) saida.set(chave, bloco);
  }
  return saida;
}
